import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Sun, Cloud, Cog, Pill, Trophy, Users, Megaphone, Info, TrendingUp, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const fmtEur0 = (n) =>
  Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 });
const fmtPct = (n) =>
  `${Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })}%`;

// 3 escenarios fijos según pantallazo
const SCENARIOS = [
  {
    key: "pesimista",
    title: "Escenario Pesimista",
    delta: 5,
    pharmacyShare: 0.9,
    teamShare: 0.1,
    Icon: Sun,
    accent: "amber",
    badge: null,
  },
  {
    key: "conservador",
    title: "Escenario Conservador",
    delta: 10,
    pharmacyShare: 0.8,
    teamShare: 0.2,
    Icon: Cloud,
    accent: "sky",
    badge: "Fácilmente conseguible",
  },
  {
    key: "optimista",
    title: "Escenario Optimista",
    delta: 15,
    pharmacyShare: 0.7,
    teamShare: 0.3,
    Icon: Cog,
    accent: "emerald",
    badge: null,
  },
];

const ACCENT = {
  amber: {
    chip: "bg-amber-100 text-amber-700",
    icon: "text-amber-500 bg-amber-50",
    value: "text-amber-500",
    border: "border-slate-200",
  },
  sky: {
    chip: "bg-sky-100 text-sky-700",
    icon: "text-sky-500 bg-sky-50",
    value: "text-sky-500",
    border: "border-sky-300",
  },
  emerald: {
    chip: "bg-emerald-100 text-emerald-700",
    icon: "text-emerald-500 bg-emerald-50",
    value: "text-emerald-500",
    border: "border-slate-200",
  },
};

function computeScenario(annualEuros, currentPct, marginPct, scenario) {
  // beneficio total 6 meses = importe_anualizado * Δ% * margen * 0.5
  const total = (annualEuros * (scenario.delta / 100) * (marginPct / 100)) / 2;
  return {
    total: Math.max(0, total),
    pharmacy: Math.max(0, total * scenario.pharmacyShare),
    team: Math.max(0, total * scenario.teamShare),
    targetPct: Math.min(100, currentPct + scenario.delta),
  };
}

function ContactDialog({ open, onOpenChange, defaultPlan }) {
  const [form, setForm] = useState({
    name: "",
    pharmacy: "",
    phone: "",
    email: "",
    plan_interest: defaultPlan || "no_idea",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, plan_interest: defaultPlan || f.plan_interest || "no_idea" }));
      setDone(false);
    }
  }, [open, defaultPlan]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Indica tu nombre");
      return;
    }
    if (!form.phone.trim() && !form.email.trim()) {
      toast.error("Déjanos al menos un teléfono o email de contacto");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "simulator" });
      setDone(true);
      toast.success("Solicitud enviada. Te contactaremos pronto.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="contact-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="contact-done">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 grid place-items-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <DialogTitle className="font-display text-2xl">¡Solicitud enviada!</DialogTitle>
            <DialogDescription className="mt-2">
              Hemos recibido tu interés. Te contactaremos en menos de 24 horas laborables.
            </DialogDescription>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="mt-6 inline-flex items-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              data-testid="contact-close"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Solicitar información</DialogTitle>
              <DialogDescription>
                Déjanos tus datos y te llamamos para activar el módulo de objetivos en tu farmacia.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Nombre*</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={change("name")}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="contact-name"
                  required
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Farmacia</span>
                <input
                  type="text"
                  value={form.pharmacy}
                  onChange={change("pharmacy")}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="contact-pharmacy"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Teléfono</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={change("phone")}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="contact-phone"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={change("email")}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="contact-email"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Plan de interés</span>
              <select
                value={form.plan_interest}
                onChange={change("plan_interest")}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
                data-testid="contact-plan"
              >
                <option value="no_idea">Aún no lo sé / Quiero información</option>
                <option value="single_use">Uso único (un estudio)</option>
                <option value="subscription_monthly">Suscripción mensual</option>
                <option value="subscription_annual">Suscripción anual</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Mensaje (opcional)</span>
              <textarea
                rows={3}
                value={form.message}
                onChange={change("message")}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                data-testid="contact-message"
                placeholder="Cuéntanos cómo te podemos ayudar…"
              />
            </label>
            <div className="text-[11px] text-slate-500">* Indica al menos un teléfono o un email para que podamos llamarte.</div>

            <DialogFooter className="!justify-between gap-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-4 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                data-testid="contact-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full transition-colors"
                data-testid="contact-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Enviar solicitud
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SimulatorPage() {
  const [loading, setLoading] = useState(true);
  const [last, setLast] = useState(null); // último estudio
  const [margin, setMargin] = useState(50); // % editable
  const [error, setError] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactPlan, setContactPlan] = useState("no_idea");

  const openContact = (plan = "no_idea") => {
    setContactPlan(plan);
    setContactOpen(true);
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/comparison/timeline");
        if (cancel) return;
        const tl = data?.timeline || [];
        setLast(tl.length ? tl[tl.length - 1] : null);
      } catch (e) {
        setError(e?.response?.data?.detail || "No se pudo cargar el último estudio");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const annualEuros = useMemo(
    () => (last ? Number(last.euros_pm || 0) * 12 : 0),
    [last]
  );
  const currentPct = useMemo(
    // % genérico EN EUROS (no en unidades) — para que el escenario calcule
    // sobre el mismo eje que el beneficio (€) y sea consistente.
    () => (last ? Number(last.generic_share_eur_pct ?? last.generic_share_pct ?? 0) : 0),
    [last]
  );
  const brandPct = useMemo(
    () => Math.max(0, 100 - currentPct),
    [currentPct]
  );

  const results = useMemo(
    () => SCENARIOS.map((s) => ({ s, r: computeScenario(annualEuros, currentPct, margin, s) })),
    [annualEuros, currentPct, margin]
  );

  const range = useMemo(() => {
    if (!results.length) return null;
    const phMin = results[0].r.pharmacy;
    const phMax = results[results.length - 1].r.pharmacy;
    const teMin = results[0].r.team;
    const teMax = results[results.length - 1].r.team;
    return { phMin, phMax, teMin, teMax };
  }, [results]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="simulator-page">
      {/* Banner azul replicando ProgramaIncentivos */}
      <div className="bg-gradient-to-r from-sky-500 to-sky-400 text-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/95 grid place-items-center">
              <Pill className="h-5 w-5 text-sky-500" strokeWidth={2} />
            </div>
            <div>
              <div className="font-display text-xl font-bold leading-tight tracking-tight">
                ProgramaIncentivos
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/85">
                y mejora tus resultados
              </div>
            </div>
          </div>
          <nav className="ml-auto flex flex-wrap items-center gap-1 text-sm">
            <a href="#simulacion" className="px-3 py-2 rounded-md hover:bg-white/10 inline-flex items-center gap-2" data-testid="nav-simulacion">
              <TrendingUp className="h-4 w-4" /> Simulación
            </a>
            <a href="#planes" className="px-3 py-2 rounded-md hover:bg-white/10 inline-flex items-center gap-2" data-testid="nav-planes">
              <Trophy className="h-4 w-4" /> Planes de incentivos
            </a>
            <a href="#equipo" className="px-3 py-2 rounded-md hover:bg-white/10 inline-flex items-center gap-2" data-testid="nav-equipo">
              <Users className="h-4 w-4" /> Equipo motivado
            </a>
            <a
              href="#contratar"
              onClick={(e) => { e.preventDefault(); openContact(); }}
              className="px-4 py-2 rounded-full bg-white text-sky-600 font-bold inline-flex items-center gap-2 hover:bg-sky-50"
              data-testid="nav-contratar"
            >
              <Megaphone className="h-4 w-4" /> ¿CÓMO CONTRATAR?
            </a>
            <a href="#mas" className="px-3 py-2 rounded-md text-white/90 hover:bg-white/10" data-testid="nav-mas">
              MÁS ADELANTE
            </a>
          </nav>
        </div>
      </div>

      <div id="simulacion" className="max-w-6xl mx-auto px-6 py-12">
        {error && (
          <div className="mb-6 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm" data-testid="sim-error">
            {error}
          </div>
        )}
        {loading ? (
          <div className="text-slate-500" data-testid="sim-loading">Cargando…</div>
        ) : !last ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="sim-no-data">
            <Sparkles className="h-8 w-8 mx-auto text-slate-400 mb-3" />
            <div className="font-display text-2xl font-bold mb-2">No hay estudios todavía</div>
            <div className="text-slate-500 max-w-md mx-auto">
              Sube una dispensación para activar el simulador de incentivos. La proyección se calcula en base a tu último estudio.
            </div>
          </div>
        ) : (
          <>
            {/* HERO */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold mb-5" data-testid="sim-badge">
                  <Sparkles className="h-3.5 w-3.5" />
                  SIMULACIÓN EN 3 ESCENARIOS
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                  ¿Cuánto ganarás con el módulo de objetivos?
                </h1>
                <p className="mt-5 text-slate-600 max-w-lg">
                  Estimamos el beneficio real en base a tres escenarios.{" "}
                  <span className="text-sky-600 font-semibold">¡Ganas tú y tu equipo!</span>
                </p>

                {/* KPIs card */}
                <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="sim-kpis">
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                        Tu importe<br/>anualizado
                      </div>
                      <div className="font-display text-3xl font-extrabold tabular-nums" data-testid="sim-kpi-annual">
                        {fmtEur0(annualEuros)}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">en base a tu estudio actual</div>
                    </div>
                    <div className="text-center border-x border-slate-100">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                        % genérico<br/>actual (€)
                      </div>
                      <div className="font-display text-3xl font-extrabold tabular-nums" data-testid="sim-kpi-generic">
                        {fmtPct(currentPct)}
                      </div>
                      <div className="text-[11px] mt-1 inline-flex items-center gap-1 text-emerald-600 font-semibold">
                        <TrendingUp className="h-3 w-3" /> {fmtPct(brandPct)} Marca (€)
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                        Margen sobre<br/>genérico
                      </div>
                      <div className="font-display text-3xl font-extrabold tabular-nums text-emerald-600" data-testid="sim-kpi-margin">
                        {margin}%
                      </div>
                      <input
                        type="range"
                        min="20"
                        max="80"
                        step="1"
                        value={margin}
                        onChange={(e) => setMargin(Number(e.target.value))}
                        className="mt-2 w-full accent-sky-500"
                        data-testid="sim-margin-slider"
                        aria-label="Margen sobre genérico"
                      />
                      <div className="text-[11px] text-slate-400 -mt-0.5">Estimación realista</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card lateral azul */}
              <div className="lg:col-span-5 lg:sticky lg:top-6">
                <div className="bg-sky-50 border border-sky-100 rounded-3xl p-7 text-center" data-testid="sim-summary-card">
                  <div className="flex items-center justify-center gap-3 mb-5">
                    <div className="h-10 w-10 rounded-md bg-sky-100 grid place-items-center"><Pill className="h-5 w-5 text-sky-500"/></div>
                    <div className="h-10 w-10 rounded-md bg-amber-100 grid place-items-center"><Trophy className="h-5 w-5 text-amber-500"/></div>
                    <div className="h-10 w-10 rounded-md bg-emerald-100 grid place-items-center"><Users className="h-5 w-5 text-emerald-500"/></div>
                  </div>
                  <div className="font-bold text-slate-900 text-lg">
                    Aumentando entre un 5% y un 15% de genéricos
                  </div>
                  <div className="text-sm text-slate-600 mt-2">
                    tu equipo se reparte entre el 10% y el 30% de esa mejora
                  </div>
                  <div className="font-display text-2xl sm:text-3xl font-extrabold text-emerald-500 mt-6 leading-tight" data-testid="sim-range-pharmacy">
                    Entre {fmtEur0(range?.phMin)} y {fmtEur0(range?.phMax)} euros para tí
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-testid="sim-range-team">
                    y entre {fmtEur0(range?.teMin)} y {fmtEur0(range?.teMax)} para tu equipo
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 2: Scenarios */}
            <section className="mt-16">
              <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 max-w-xl">
                  Beneficio simulado para 6 meses en base a tres escenarios
                </h2>
                <div className="text-xs text-slate-500 inline-flex items-center gap-2 max-w-xs">
                  <Info className="h-4 w-4 text-slate-400 shrink-0" />
                  <span>Basado en tus datos reales — Incremento en dispensación de genéricos</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="sim-scenarios">
                {results.map(({ s, r }) => {
                  const a = ACCENT[s.accent];
                  return (
                    <div
                      key={s.key}
                      className={`relative bg-white rounded-2xl border-2 ${a.border} p-6 transition-all hover:-translate-y-1 hover:shadow-lg`}
                      data-testid={`sim-card-${s.key}`}
                    >
                      {s.badge && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-[11px] font-bold px-3 py-1 rounded-full shadow">
                          {s.badge}
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <div className={`h-11 w-11 rounded-full grid place-items-center ${a.icon}`}>
                          <s.Icon className="h-5 w-5" strokeWidth={2} />
                        </div>
                        <div>
                          <div className="font-bold text-slate-900">{s.title}</div>
                          <div className={`inline-block mt-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.chip}`}>
                            +{s.delta}% genéricos (€) – {fmtPct(r.targetPct)}
                          </div>
                        </div>
                      </div>
                      <div className={`mt-6 font-display text-5xl font-extrabold tabular-nums ${a.value}`} data-testid={`sim-card-${s.key}-total`}>
                        {fmtEur0(r.total)} <span className="text-2xl font-bold">euros</span>
                      </div>
                      <div className="mt-4 text-sm text-slate-600 space-y-1.5">
                        <div>
                          un <span className="font-bold text-slate-900">{Math.round(s.pharmacyShare * 100)}%</span> para la farmacia (
                          <span className="font-semibold tabular-nums" data-testid={`sim-card-${s.key}-pharmacy`}>{fmtEur0(r.pharmacy)}</span>)
                        </div>
                        <div>
                          y un <span className="font-bold text-slate-900">{Math.round(s.teamShare * 100)}%</span> para tu equipo (
                          <span className="font-semibold tabular-nums" data-testid={`sim-card-${s.key}-team`}>{fmtEur0(r.team)}</span>)
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* SECTION 3: Todos ganan */}
            <section className="mt-16" id="planes">
              <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-2 border-emerald-100 rounded-3xl p-8 sm:p-10" data-testid="sim-everyone-wins">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 grid place-items-center text-2xl">😊</div>
                  <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                    Todos ganan con este módulo
                  </h2>
                </div>
                <p className="text-slate-600 max-w-2xl mb-8">
                  Un sistema de incentivos justo, transparente y orientado a resultados:
                  cuanto más crece la dispensación de genéricos, más gana la farmacia
                  <strong> y</strong> más gana el equipo.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Farmacia */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="sim-summary-pharmacy">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-2xl bg-sky-100 grid place-items-center">
                        <Pill className="h-6 w-6 text-sky-500" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Lo que gana</div>
                        <div className="font-display text-xl font-extrabold">La farmacia</div>
                      </div>
                    </div>
                    <div className="font-display text-4xl font-extrabold text-sky-500 tabular-nums">
                      {fmtEur0(range?.phMin)} – {fmtEur0(range?.phMax)} €
                    </div>
                    <div className="text-sm text-slate-500 mt-1">en 6 meses, según escenario</div>
                    <ul className="mt-5 space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0"/>
                        <span>+{SCENARIOS[0].delta}% genéricos → reparte el {Math.round(SCENARIOS[0].pharmacyShare * 100)}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0"/>
                        <span>+{SCENARIOS[1].delta}% genéricos → reparte el {Math.round(SCENARIOS[1].pharmacyShare * 100)}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0"/>
                        <span>+{SCENARIOS[2].delta}% genéricos → reparte el {Math.round(SCENARIOS[2].pharmacyShare * 100)}%</span>
                      </li>
                    </ul>
                  </div>

                  {/* Equipo */}
                  <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="sim-summary-team">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-12 w-12 rounded-2xl bg-emerald-100 grid place-items-center">
                        <Users className="h-6 w-6 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">Lo que gana</div>
                        <div className="font-display text-xl font-extrabold">El equipo</div>
                      </div>
                    </div>
                    <div className="font-display text-4xl font-extrabold text-emerald-500 tabular-nums">
                      {fmtEur0(range?.teMin)} – {fmtEur0(range?.teMax)} €
                    </div>
                    <div className="text-sm text-slate-500 mt-1">en 6 meses, repartidos entre el equipo</div>
                    <ul className="mt-5 space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0"/>
                        <span>+{SCENARIOS[0].delta}% genéricos → reparte el {Math.round(SCENARIOS[0].teamShare * 100)}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0"/>
                        <span>+{SCENARIOS[1].delta}% genéricos → reparte el {Math.round(SCENARIOS[1].teamShare * 100)}%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0"/>
                        <span>+{SCENARIOS[2].delta}% genéricos → reparte el {Math.round(SCENARIOS[2].teamShare * 100)}%</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div id="contratar" className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={() => openContact()}
                    className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-bold px-7 py-3.5 rounded-full transition-colors shadow-lg shadow-sky-500/30"
                    data-testid="sim-cta-contratar"
                  >
                    <Megaphone className="h-4 w-4" /> ¿CÓMO CONTRATAR?
                  </button>
                  <div className="text-xs text-slate-500 mt-3">
                    Activa el módulo de objetivos y empieza a repartir incentivos basados en datos reales.
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} defaultPlan={contactPlan} />
    </div>
  );
}
